import type { ToolSlug } from '../data/tools';

export type BillingDetails = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  taxNumber: string;
};


async function readResponseBody(response: Response) {
  const text = await response.text();
  if (!text) return {} as Record<string, unknown>;
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return { error: text.slice(0, 300) } as Record<string, unknown>;
  }
}

function networkError(error: unknown, action: string) {
  const message = error instanceof Error ? error.message : '';
  if (/failed to fetch|networkerror|load failed/i.test(message)) {
    return new Error(`The local backend is not responding. Run npm install, restart npm run dev, and try ${action} again.`);
  }
  return error instanceof Error ? error : new Error(`Could not ${action}.`);
}

export type ProcessResult = {
  jobId: string;
  outputUrl: string;
  downloadUrl: string;
  inputUrl?: string;
  width?: number;
  height?: number;
  creditsUsed: number;
  creditsRemaining?: number | null;
};

export async function processImage(params: {
  file: File;
  tool: ToolSlug;
  options: Record<string, string | number | boolean>;
  accessToken?: string;
}): Promise<ProcessResult> {
  const form = new FormData();
  form.append('image', params.file);
  form.append('tool', params.tool);
  form.append('options', JSON.stringify(params.options));

  try {
    const response = await fetch('/api/process', {
      method: 'POST',
      headers: params.accessToken ? { Authorization: `Bearer ${params.accessToken}` } : undefined,
      body: form
    });

    const body = await readResponseBody(response) as { error?: string } & Partial<ProcessResult>;
    if (!response.ok) throw new Error(body.error || (response.status >= 500 ? 'The local backend is not responding. Run npm install and restart npm run dev.' : `Image processing failed with status ${response.status}.`));
    return body as ProcessResult;
  } catch (error) {
    throw networkError(error, 'processing');
  }
}

export async function createCheckout(
  plan: 'starter' | 'creator' | 'studio',
  billing: 'monthly' | 'yearly',
  billingDetails: BillingDetails,
  accessToken?: string
) {
  const response = await fetch('/api/billing/checkout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
    },
    body: JSON.stringify({ plan, billing, billingDetails })
  });
  const body = (await response.json().catch(() => ({}))) as { url?: string; error?: string };
  if (!response.ok || !body.url) throw new Error(body.error || 'Could not prepare checkout.');
  return { url: body.url };
}


export async function getBillingPortal(accessToken?: string) {
  if (!accessToken) throw new Error('Sign in before opening billing management.');
  const response = await fetch('/api/billing/portal', {
    headers: { Authorization: `Bearer ${accessToken}` }
  });
  const body = (await response.json().catch(() => ({}))) as { url?: string; error?: string };
  if (!response.ok || !body.url) throw new Error(body.error || 'Could not open the billing portal.');
  return body.url;
}

export type AccountProfileResponse = {
  id: string;
  email: string;
  plan: string;
  credits: number;
  monthlyCreditAllowance: number;
  subscriptionStatus?: string | null;
  billingInterval?: 'monthly' | 'yearly' | null;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string | null;
};

export async function getProfile(accessToken?: string): Promise<AccountProfileResponse> {
  if (!accessToken) throw new Error('Sign in before loading account credits.');

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 12_000);
  try {
    const response = await fetch('/api/me', {
      headers: { Authorization: `Bearer ${accessToken}` },
      signal: controller.signal,
      cache: 'no-store'
    });
    const body = await readResponseBody(response) as Partial<AccountProfileResponse> & { error?: string };
    if (!response.ok) {
      throw new Error(body.error || `Could not load the account balance (status ${response.status}).`);
    }
    if (typeof body.credits !== 'number' || !body.id || !body.plan) {
      throw new Error('The account service returned an incomplete credit profile.');
    }
    return body as AccountProfileResponse;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('The account service took too long to respond.');
    }
    throw error instanceof Error ? error : new Error('Could not load the account balance.');
  } finally {
    window.clearTimeout(timeout);
  }
}



export type ContactPayload = {
  name: string;
  email: string;
  subject: string;
  category: string;
  orderReference?: string;
  message: string;
};

export async function submitContact(payload: ContactPayload) {
  try {
    const response = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const body = await readResponseBody(response) as { ok?: boolean; error?: string };
    if (!response.ok || !body.ok) throw new Error(body.error || (response.status >= 500 ? 'The local backend is not responding. Run npm install and restart npm run dev.' : `Could not send your message (status ${response.status}).`));
    return body;
  } catch (error) {
    throw networkError(error, 'sending your message');
  }
}

export type RecentJob = {
  id: string;
  tool: ToolSlug;
  status: 'processing' | 'completed' | 'failed';
  creditCost: number;
  outputUrl?: string | null;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
};

export async function getRecentJobs(accessToken?: string) {
  if (!accessToken) return [] as RecentJob[];
  const response = await fetch('/api/me/jobs', { headers: { Authorization: `Bearer ${accessToken}` } });
  if (!response.ok) return [] as RecentJob[];
  const body = await response.json() as { jobs?: RecentJob[] };
  return body.jobs ?? [];
}
